import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Table } from '@shared/components';
import { DOCUMENT_TYPE_LABELS } from '@/features/document-templates/utils/documentTypeLabels';
import type { GeneratedDocument } from '../../types/agreementDocuments.types';
import { formatOccurredAt } from '../../utils/timelineEntryLabels';
import { triggerLabel } from '../../utils/generatedDocumentDisplay';
import { DocumentRowActions } from './DocumentRowActions';
import { DocumentStatusCell } from './DocumentStatusCell';
import { TemplateCell } from './TemplateCell';
import type { DocumentsLayoutProps } from './documentsTabView.types';

type Props = Omit<DocumentsLayoutProps, 'canGenerate' | 'onGenerate'>;

export function AgreementDocumentsDataTable({ documents, now, canRead, onView, onDownload, onDetails }: Props) {
    const columns = useMemo<ColumnDef<GeneratedDocument>[]>(
        () => [
            {
                id: 'document',
                header: 'Document',
                cell: ({ row }) => <span className="whitespace-nowrap font-medium">{DOCUMENT_TYPE_LABELS[row.original.documentType]}</span>,
            },
            { id: 'status', header: 'Status', cell: ({ row }) => <DocumentStatusCell document={row.original} now={now} /> },
            {
                id: 'generated',
                header: 'Generated',
                cell: ({ row }) => (row.original.generatedAt ? <span className="whitespace-nowrap">{formatOccurredAt(row.original.generatedAt)}</span> : <span className="text-mp-muted">—</span>),
            },
            {
                id: 'trigger',
                header: 'Trigger',
                cell: ({ row }) => (
                    <div>
                        {triggerLabel(row.original.trigger)}
                        {row.original.requestedBy && <span className="block text-xs text-mp-muted">by {row.original.requestedBy.name ?? 'unknown user'}</span>}
                    </div>
                ),
            },
            {
                id: 'template',
                header: 'Template',
                cell: ({ row }) => (row.original.template ? <TemplateCell template={row.original.template} /> : <span className="text-mp-muted">—</span>),
            },
            {
                id: 'actions',
                header: () => <span className="sr-only">Actions</span>,
                cell: ({ row }) => (
                    <DocumentRowActions
                        document={row.original}
                        label={DOCUMENT_TYPE_LABELS[row.original.documentType]}
                        canRead={canRead}
                        onView={onView}
                        onDownload={onDownload}
                        onDetails={onDetails}
                    />
                ),
            },
        ],
        [now, canRead, onView, onDownload, onDetails],
    );

    return (
        <Table<GeneratedDocument>
            data={[...documents]}
            columns={columns}
            getRowId={(row) => row.id}
            emptyMessage="No documents have been generated for this agreement."
            framed={false}
        />
    );
}
